import {useCallback, useEffect, useRef} from 'react';

/**
 * window에 추가된 이벤트 리스너가 실행되지 않도록 하는 훅입니다.
 *
 * 리턴된 Ref의 current를 true로 해두면, 이벤트 버블로 window의 이벤트가 실행되기 전에 이 훅의 이벤트가 실행되면서 window의 이벤트를 막습니다.
 * 블락할 window의 이벤트가 이 이벤트보다 뒤에 실행되어야 합니다.
 * 대체로 DOM 계층 구조에서 캡쳐시 이벤트가 실행되게 등록하지 않은 이상, 나중에 window에 추가된 이벤트가 먼저 호출됩니다.
 */
export function useWindowEventBlockerRef<K extends keyof WindowEventMap>(
  type: K
) {
  const shouldEventBlockRef = useRef(false);

  const handler = useCallback((event: WindowEventMap[K]) => {
    if (shouldEventBlockRef.current) {
      event.stopImmediatePropagation();
      shouldEventBlockRef.current = false;
    }
  }, []);

  useEffect(() => {
    window.addEventListener(type, handler);

    return () => {
      window.removeEventListener(type, handler);
    };
  }, [handler, type]);

  return shouldEventBlockRef;
}
